import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarClock, XCircle, PhoneCall, CreditCard } from "lucide-react";
import { api } from "@/lib/api";
import { ManageActions } from "@/components/booking/ManageActions";
import { OnsiteRequest } from "@/components/booking/OnsiteRequest";

export default function ManageBooking() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/bookings/public/${token}`).then((r) => setB(r.data)).catch(() => setError("We couldn't find that booking."));
  }, [token]);

  if (error) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-300" data-testid="manage-notfound">{error} <Link to="/book" className="dg-link">Book a session</Link></div>;
  if (!b) return <div className="max-w-2xl mx-auto px-4 py-20 text-center text-slate-400">Loading your booking…</div>;
  const cancelled = b.status === "cancelled";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="manage-booking">
      <div className="dg-card rounded-2xl p-8">
        <div className="flex items-start gap-4">
          {cancelled ? <XCircle className="w-10 h-10 text-rose-400 shrink-0" /> : <CalendarClock className="w-10 h-10 text-cyan-400 shrink-0" />}
          <div>
            <div className="dg-eyebrow">{cancelled ? "Cancelled" : "Your session"}</div>
            <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold font-display" data-testid="manage-title">{cancelled ? "This session was cancelled." : `Hi ${b.name}, here's your booking.`}</h1>
            <p className="text-slate-300 mt-3"><strong className="text-white">{b.service_name}</strong><br /><span data-testid="manage-when">{b.when}</span></p>
            <p className="text-slate-400 mt-1 text-sm">{b.meeting_method} · Booking <span className="font-mono text-cyan-300">{b.booking_id}</span>{b.payment_status === "paid" && <span className="dg-chip ml-2 text-emerald-300">Paid</span>}</p>
          </div>
        </div>

        {!cancelled && b.amount_due > 0 && (
          <div className="mt-6 rounded-xl border border-[#FFD600]/40 bg-[#FFD600]/10 p-4 text-sm flex flex-wrap items-center gap-3" data-testid="manage-pay-box">
            <CreditCard className="w-4 h-4 text-[#FFD600] shrink-0" />
            <div className="flex-1 min-w-[200px] text-slate-100">{b.payment_requested_at ? <>David sent your payment link — <strong>${Number(b.amount_due).toFixed(2)}</strong> is due for this session.</> : <>Nothing due yet — you'll get a secure card link after the session (<strong>${Number(b.amount_due).toFixed(2)}</strong>).</>}</div>
            <Link to={`/pay/${token}`} className="dg-btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2" data-testid="manage-pay-btn"><CreditCard className="w-4 h-4" /> {b.payment_requested_at ? `Pay $${Number(b.amount_due).toFixed(2)}` : "Pay ahead"}</Link>
          </div>
        )}

        {cancelled && <div className="mt-6"><Link to="/book" className="dg-btn-primary rounded-full px-6 py-3 text-sm inline-flex" data-testid="manage-rebook-btn">Book a new time</Link></div>}
        {!cancelled && b.can_modify && <ManageActions token={token} booking={b} onChange={setB} />}
        {!cancelled && <OnsiteRequest token={token} booking={b} onChange={setB} />}
        {!cancelled && !b.can_modify && !b.can_request_onsite && !b.onsite_request && (
          <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100 flex items-start gap-3" data-testid="manage-locked">
            <PhoneCall className="w-4 h-4 mt-0.5 shrink-0" />
            <div>Changes within {b.modify_cutoff_hours} hours of a session need a quick call — <a href="tel:+13104220669" className="dg-link">310-422-0669</a>. David will sort it out.</div>
          </div>
        )}
      </div>
    </div>
  );
}
